import { z } from "zod";
import {
  ACCOUNT_KINDS,
  CATEGORY_KINDS,
  DRE_GROUPS,
  MAX_ACCOUNT_BALANCE_CENTS,
  MAX_BULK_SETTLE,
  MAX_FINANCE_PCT4,
  MAX_PRICE_CENTS,
  MAX_SETTLEMENT_DAYS,
  MAX_TITLE_CENTS,
  OCCURRENCES,
  SEFAZ_PAYMENT_TYPES,
  dreGroupsFor,
  occurrenceProblem,
  type AccountKind,
  type BankTxKind,
  type CategoryKind,
  type DreGroup,
  type Occurrence,
  type SefazPaymentType,
  type TitleSide,
  type TitleStatus,
} from "@cx/domain";
import { listQuery } from "./common";
import { blank, text } from "./fields";
import { ulidParam } from "./ids";
import { businessDate } from "./prices";

// ===== Financeiro (T1.9, RF-FIN-01/02/06/07) =====

const nullish = <T extends z.ZodType>(s: T) => s.nullish().transform((v) => v ?? null);
const name = (max: number) => z.string().trim().min(1, "Informe o nome").max(max, `Até ${max} caracteres`);
const money = (what: string, max = MAX_TITLE_CENTS) =>
  z
    .number(`Informe ${what}`)
    .int("Use centavos inteiros")
    .min(0, "Valor não pode ser negativo")
    .max(max, "Valor acima do limite");
const positive = (what: string) => money(what).min(1, `Informe ${what}`);
const pct = (what: string) =>
  z
    .number(`Informe ${what}`)
    .int("Percentual inválido")
    .min(0, "O percentual vai de 0% a 100%")
    .max(MAX_FINANCE_PCT4, "O percentual vai de 0% a 100%");
const optDate = blank.pipe(businessDate.nullable());

// ---------- formas de pagamento ----------

export const paymentMethodInput = z.object({
  name: name(60),
  sefazType: z.enum(SEFAZ_PAYMENT_TYPES, "Tipo de pagamento inválido"),
  feePct4: pct("a taxa").default(0),
  feeFixedCents: money("a taxa fixa", MAX_PRICE_CENTS).default(0),
  settlementDays: z
    .number("Informe o prazo")
    .int("Prazo inválido")
    .min(0, `De 0 a ${MAX_SETTLEMENT_DAYS} dias`)
    .max(MAX_SETTLEMENT_DAYS, `De 0 a ${MAX_SETTLEMENT_DAYS} dias`)
    .default(0),
  defaultAccountId: nullish(ulidParam),
  active: z.boolean().default(true),
});
export type PaymentMethodInput = z.output<typeof paymentMethodInput>;
export type PaymentMethodFormValues = z.input<typeof paymentMethodInput>;
export type PaymentMethodView = PaymentMethodInput & {
  id: string;
  sefazType: SefazPaymentType;
  defaultAccountName: string | null;
};

// ---------- categorias financeiras ----------

/** Edição: o tipo (receita/despesa) é fixo depois de criada. */
export const financialCategoryInput = z.object({
  name: name(80),
  parentId: nullish(ulidParam),
  dreGroup: z.enum(DRE_GROUPS, "Grupo de DRE inválido"),
  active: z.boolean().default(true),
});
export type FinancialCategoryInput = z.output<typeof financialCategoryInput>;

export const financialCategoryCreateInput = financialCategoryInput
  .extend({ kind: z.enum(CATEGORY_KINDS, "Tipo inválido") })
  .superRefine((v, ctx) => {
    if (!(CATEGORY_KINDS as readonly string[]).includes(v.kind)) return;
    if (!dreGroupsFor(v.kind).includes(v.dreGroup))
      ctx.addIssue({ code: "custom", path: ["dreGroup"], message: "Grupo de DRE não combina com o tipo" });
  });
export type FinancialCategoryCreateInput = z.output<typeof financialCategoryCreateInput>;
export type FinancialCategoryFormValues = z.input<typeof financialCategoryCreateInput>;
export const financialCategoryQuery = z.object({ kind: z.enum(CATEGORY_KINDS, "Tipo inválido").optional() });

export type FinancialCategoryView = {
  id: string;
  parentId: string | null;
  name: string;
  kind: CategoryKind;
  dreGroup: DreGroup;
  isSystem: boolean;
  active: boolean;
  /** 1 = raiz. */
  depth: number;
  /** "Pai › Filho". */
  path: string;
};

// ---------- contas financeiras (caixas e bancos) ----------

export const financialAccountInput = z.object({
  name: name(60),
  kind: z.enum(ACCOUNT_KINDS, "Tipo de conta inválido"),
  bankCode: blank.pipe(
    z
      .string()
      .regex(/^\d{3}$/, "Use o código de 3 dígitos do banco")
      .nullable(),
  ),
  agency: text(10),
  accountNumber: text(20),
  openingBalanceCents: z
    .number("Informe o saldo inicial")
    .int("Use centavos inteiros")
    .min(-MAX_ACCOUNT_BALANCE_CENTS, "Valor acima do limite")
    .max(MAX_ACCOUNT_BALANCE_CENTS, "Valor acima do limite")
    .default(0),
  openingDate: optDate,
  active: z.boolean().default(true),
});
export type FinancialAccountInput = z.output<typeof financialAccountInput>;
export type FinancialAccountFormValues = z.input<typeof financialAccountInput>;
export type FinancialAccountView = FinancialAccountInput & {
  id: string;
  kind: AccountKind;
  /** Saldo atual (inicial + lançamentos); null se passar de 2^53 (a tela mostra "—"). */
  balanceCents: number | null;
};

/** Lançamento manual: entrada ou saída, valor positivo. */
export const bankTransactionInput = z.object({
  direction: z.enum(["in", "out"], "Informe entrada ou saída"),
  date: businessDate,
  amountCents: positive("o valor"),
  description: text(200),
  financialCategoryId: nullish(ulidParam),
  contactId: nullish(ulidParam),
});
export type BankTransactionInput = z.output<typeof bankTransactionInput>;
export type BankTransactionFormValues = z.input<typeof bankTransactionInput>;

export const bankTransactionQuery = listQuery.omit({ q: true }).extend({
  from: businessDate.optional(),
  to: businessDate.optional(),
});
export type BankTransactionQuery = z.output<typeof bankTransactionQuery>;

export const reverseInput = z.object({ notes: text(200) });
export type ReverseInput = z.output<typeof reverseInput>;

export type BankTransactionView = {
  id: string;
  financialAccountId: string;
  date: string;
  amountCents: number;
  description: string | null;
  kind: BankTxKind;
  financialCategoryId: string | null;
  categoryName: string | null;
  contactId: string | null;
  contactName: string | null;
  transferGroupId: string | null;
  titleId: string | null;
  settlementId: string | null;
  reversalOfId: string | null;
  reversedAt: string | null;
  createdAt: string;
};

/** Extrato: lançamentos do período (paginados) e o resumo do período (saldo anterior, entradas, saídas, saldo final). */
export type AccountStatement = {
  data: BankTransactionView[];
  nextCursor: string | null;
  period: {
    openingCents: number | null;
    inCents: number | null;
    outCents: number | null;
    closingCents: number | null;
  };
};

export const transferInput = z
  .object({
    fromAccountId: ulidParam,
    toAccountId: ulidParam,
    date: businessDate,
    amountCents: positive("o valor"),
    description: text(200),
  })
  .refine((v) => v.fromAccountId !== v.toAccountId, {
    path: ["toAccountId"],
    message: "Escolha uma conta de destino diferente da origem",
  });
export type TransferInput = z.output<typeof transferInput>;

export type TransferView = {
  groupId: string;
  date: string;
  amountCents: number;
  description: string | null;
  fromAccountId: string;
  fromAccountName: string;
  toAccountId: string;
  toAccountName: string;
  /** Estornada (o par inverso existe). */
  reversedAt: string | null;
  /** É o par inverso de outra transferência. */
  isReversal: boolean;
  createdAt: string;
};
export const transferListQuery = listQuery.omit({ q: true });

// ---------- títulos (contas a receber e a pagar) ----------

const titleFields = {
  contactId: ulidParam,
  amountCents: positive("o valor"),
  issueDate: businessDate,
  competenceDate: businessDate,
  dueDate: businessDate,
  description: text(2000),
  paymentMethodId: nullish(ulidParam),
  financialAccountId: nullish(ulidParam),
  bankNumber: text(30),
  documentNumber: text(30),
  financialCategoryId: nullish(ulidParam),
  sellerId: nullish(ulidParam),
  interestMonthPct4: pct("os juros").default(0),
  finePct4: pct("a multa").default(0),
};

/** Edição (sem ocorrência: cada parcela/ocorrência é um título). */
export const titleInput = z.object(titleFields);
export type TitleInput = z.output<typeof titleInput>;

/**
 * Inclusão: `occurrence` + `count` geram as parcelas (parcelada) ou ocorrências (recorrente) a partir do vencimento;
 * `weekday` (0 = domingo) só na semanal.
 */
export const titleCreateInput = z
  .object({
    ...titleFields,
    occurrence: z.enum(OCCURRENCES, "Ocorrência inválida").default("single"),
    count: z.number("Informe a quantidade").int("Quantidade inválida").default(1),
    weekday: nullish(
      z
        .number()
        .int("Dia da semana inválido")
        .min(0, "Dia da semana inválido")
        .max(6, "Dia da semana inválido"),
    ),
  })
  .superRefine((v, ctx) => {
    if (!(OCCURRENCES as readonly string[]).includes(v.occurrence)) return;
    const problem = occurrenceProblem(v.occurrence, v.count);
    if (problem) ctx.addIssue({ code: "custom", path: ["count"], message: problem });
    if (v.weekday !== null && v.occurrence !== "weekly")
      ctx.addIssue({ code: "custom", path: ["weekday"], message: "Dia da semana só na ocorrência semanal" });
  });
export type TitleCreateInput = z.output<typeof titleCreateInput>;
export type TitleFormValues = z.input<typeof titleCreateInput>;

export const TITLE_FILTERS = ["open", "overdue", "paid", "canceled", "all"] as const;
export type TitleFilter = (typeof TITLE_FILTERS)[number];
export const TITLE_DATE_FIELDS = ["due", "issue", "competence", "paid"] as const;
export type TitleDateField = (typeof TITLE_DATE_FIELDS)[number];

export const titleListQuery = listQuery.extend({
  /** Em aberto (aberto ou parcial), atrasadas, recebidas/pagas, canceladas ou todas. */
  status: z.enum(TITLE_FILTERS, "Filtro inválido").default("open"),
  dateField: z.enum(TITLE_DATE_FIELDS, "Campo de data inválido").default("due"),
  from: businessDate.optional(),
  to: businessDate.optional(),
  contactId: ulidParam.optional(),
  paymentMethodId: ulidParam.optional(),
});
export type TitleListQuery = z.output<typeof titleListQuery>;

export type TitleView = {
  id: string;
  side: TitleSide;
  contactId: string;
  contactName: string;
  amountCents: number;
  paidCents: number;
  openCents: number;
  issueDate: string;
  competenceDate: string;
  dueDate: string;
  originalDueDate: string | null;
  description: string | null;
  occurrence: Occurrence;
  recurrenceGroupId: string | null;
  installmentNumber: number | null;
  installmentTotal: number | null;
  paymentMethodId: string | null;
  paymentMethodName: string | null;
  financialAccountId: string | null;
  financialAccountName: string | null;
  bankNumber: string | null;
  documentNumber: string | null;
  financialCategoryId: string | null;
  categoryName: string | null;
  sellerId: string | null;
  sellerName: string | null;
  interestMonthPct4: number;
  finePct4: number;
  status: TitleStatus;
  overdue: boolean;
  lastPaidAt: string | null;
  originType: string | null;
  originId: string | null;
  /** Ex.: "Pedido de venda nº 12". */
  originLabel: string | null;
  /** Documento a abrir pela origem quando difere de `originId` (recebimento → pedido de compra, T2.6). */
  originDocumentId?: string | null;
  createdAt: string;
};

export type SettlementView = {
  id: string;
  paidAt: string;
  amountCents: number;
  interestCents: number;
  fineCents: number;
  discountCents: number;
  feeCents: number;
  /** Valor − desconto + juros + multa. */
  paidCents: number;
  financialAccountId: string;
  financialAccountName: string;
  paymentMethodId: string | null;
  notes: string | null;
  createdAt: string;
  reversedAt: string | null;
};

export type TitleDetail = TitleView & {
  settlements: SettlementView[];
  /** Juros e multa se a baixa total fosse hoje (painel do formulário). */
  chargesToday: { daysLate: number; interestCents: number; fineCents: number; totalCents: number };
};

export type TitleListResult = {
  data: TitleView[];
  nextCursor: string | null;
  /** Só na primeira página; nas com `cursor`, count = -1 e os totais null. */
  summary: { count: number; totalCents: number | null; openCents: number | null };
};

export type TitleCreated = { ids: string[]; recurrenceGroupId: string | null };

/**
 * Baixa. `amountCents` = parte do saldo quitada; juros e multa nulos = calculados (05 §7) sobre o valor da baixa;
 * taxa nula = a da forma de pagamento (taxa % sobre o pago + taxa fixa).
 */
export const settleInput = z
  .object({
    paidAt: businessDate,
    amountCents: positive("o valor da baixa"),
    discountCents: money("o desconto").default(0),
    interestCents: nullish(money("os juros")),
    fineCents: nullish(money("a multa")),
    feeCents: nullish(money("a taxa")),
    financialAccountId: ulidParam,
    paymentMethodId: nullish(ulidParam),
    notes: text(500),
  })
  .refine((v) => !(v.discountCents > v.amountCents), {
    path: ["discountCents"],
    message: "O desconto não pode passar do valor da baixa",
  });
export type SettleInput = z.output<typeof settleInput>;
export type SettleFormValues = z.input<typeof settleInput>;

export const bulkSettleInput = z.object({
  paidAt: businessDate,
  financialAccountId: ulidParam,
  ids: z
    .array(ulidParam, "Selecione as contas")
    .min(1, "Selecione ao menos uma conta")
    .max(MAX_BULK_SETTLE, `No máximo ${MAX_BULK_SETTLE} contas por vez`)
    .refine((l) => new Set(l).size === l.length, "Conta repetida na seleção"),
});
export type BulkSettleInput = z.output<typeof bulkSettleInput>;
export type BulkSettleResult = {
  results: { id: string; ok: boolean; code?: string; message?: string }[];
  okCount: number;
  failCount: number;
};

export type TitleFormOptions = {
  accounts: { id: string; name: string; active: boolean }[];
  categories: { id: string; path: string; active: boolean }[];
  paymentMethods: {
    id: string;
    name: string;
    active: boolean;
    feePct4: number;
    feeFixedCents: number;
    defaultAccountId: string | null;
  }[];
  sellers: { id: string; name: string }[];
  /** Usuário-vendedor: o vendedor do título é sempre ele (o servidor força). */
  sellerLocked: boolean;
};
